import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Send, Sparkles, X } from 'lucide-react'

import { api } from './api/client'
import type { AssistantMessage } from './api/types'
import { Button } from './components/ui/button'
import { Input } from './components/ui/input'
import { cn } from './lib/utils'

interface ChatTurn extends AssistantMessage {
  toolCount?: number
}

export default function Assistant() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState<ChatTurn[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [open, history, busy])

  const suggestions = (t('assistant.suggestions', { returnObjects: true }) as unknown) as string[]

  async function send(text: string) {
    const message = text.trim()
    if (!message || busy) return
    setHistory((h) => [...h, { role: 'user', content: message }])
    setInput('')
    setBusy(true)
    try {
      const r = await api.assistantChat({
        message,
        history: history.map((h) => ({ role: h.role, content: h.content })),
      })
      setHistory((h) => [
        ...h,
        { role: 'assistant', content: r.text || '…', toolCount: r.tool_calls.length },
      ])
    } catch {
      setHistory((h) => [...h, { role: 'assistant', content: t('errors.generic') }])
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {/* Floating action button */}
      <Button
        size="pill"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="fixed bottom-5 right-5 z-40 shadow-[0_8px_28px_-6px_rgba(47,122,85,0.45)]"
      >
        {open ? <X className="size-4" /> : <Sparkles className="size-4" />}
        {open ? t('assistant.close') : t('assistant.fab')}
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label={t('assistant.title')}
          className="fixed bottom-20 right-5 z-40 flex w-[min(420px,calc(100vw-40px))] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-[0_20px_50px_-15px_rgba(27,29,26,0.25)] animate-in"
          style={{ height: 'min(560px, calc(100vh - 120px))' }}
        >
          <div className="border-b border-border bg-secondary/60 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="size-4 text-primary" />
              {t('assistant.title')}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{t('assistant.sub')}</div>
          </div>

          <div
            ref={scrollRef}
            className="flex flex-1 flex-col gap-2.5 overflow-y-auto p-4"
          >
            {history.length === 0 && (
              <div className="space-y-2 text-xs text-muted-foreground">
                <div>{t('assistant.emptyHint')}</div>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="block w-full rounded-md border border-border bg-card px-3 py-2 text-left text-[12.5px] text-foreground hover:bg-secondary"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {history.map((m, i) => (
              <div
                key={i}
                className={cn(
                  'max-w-[88%] whitespace-pre-wrap rounded-xl px-3 py-2 text-[13.5px] leading-relaxed',
                  m.role === 'user'
                    ? 'self-end bg-primary text-primary-foreground'
                    : 'self-start bg-secondary text-foreground',
                )}
              >
                {m.content}
                {m.role === 'assistant' && (m.toolCount ?? 0) > 0 && (
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {t('assistant.toolsUsed', { count: m.toolCount })}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="self-start rounded-xl bg-secondary px-3 py-2 text-sm text-muted-foreground">
                <Loader2 className="mr-2 inline size-3.5 animate-spin" />
                {t('assistant.thinking')}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              void send(input)
            }}
            className="flex gap-2 border-t border-border p-3"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('assistant.placeholder')}
              disabled={busy}
              className="flex-1"
            />
            <Button type="submit" disabled={busy || !input.trim()} size="icon">
              <Send className="size-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  )
}
